export type PublishedBounds2 = {
  readonly minX: number
  readonly minZ: number
  readonly maxX: number
  readonly maxZ: number
}

export type ResolvedStreamingMotion = {
  readonly x: number
  readonly z: number
  readonly collided: boolean
}

export type StreamingTraversalPlan = {
  /** The simulation target. Published coverage never behaves like a wall. */
  readonly x: number
  readonly z: number
  /** True when streaming should urgently publish terrain around the target. */
  readonly coveragePending: boolean
}

function finiteBounds(bounds: PublishedBounds2): boolean {
  return Number.isFinite(bounds.minX)
    && Number.isFinite(bounds.minZ)
    && Number.isFinite(bounds.maxX)
    && Number.isFinite(bounds.maxZ)
    && bounds.minX <= bounds.maxX
    && bounds.minZ <= bounds.maxZ
}

/**
 * Conservatively treats the vehicle footprint as an axis-aligned square and
 * proves that the complete square is covered by the union of published chunk
 * bounds. The square is deliberately stricter than the vehicle's circular
 * footprint: an unpublished concave corner remains a hard streaming boundary.
 */
export function publishedEnvelopeContains(
  publishedBounds: readonly PublishedBounds2[],
  x: number,
  z: number,
  radiusMeters: number,
  chunkPaddingMeters = 0.02,
): boolean {
  if (![x, z, radiusMeters, chunkPaddingMeters].every(Number.isFinite)) return false
  const radius = Math.max(0, radiusMeters)
  const padding = Math.max(0, chunkPaddingMeters)
  const query = {
    minX: x - radius,
    minZ: z - radius,
    maxX: x + radius,
    maxZ: z + radius,
  }
  const clipped: PublishedBounds2[] = []
  for (const source of publishedBounds) {
    if (!finiteBounds(source)) continue
    const expanded = {
      minX: source.minX - padding,
      minZ: source.minZ - padding,
      maxX: source.maxX + padding,
      maxZ: source.maxZ + padding,
    }
    if (expanded.maxX < query.minX || expanded.minX > query.maxX || expanded.maxZ < query.minZ || expanded.minZ > query.maxZ) continue
    clipped.push({
      minX: Math.max(query.minX, expanded.minX),
      minZ: Math.max(query.minZ, expanded.minZ),
      maxX: Math.min(query.maxX, expanded.maxX),
      maxZ: Math.min(query.maxZ, expanded.maxZ),
    })
  }
  if (clipped.length === 0) return false
  if (radius === 0) {
    return clipped.some((bounds) => x >= bounds.minX && x <= bounds.maxX && z >= bounds.minZ && z <= bounds.maxZ)
  }

  const xBreaks = [...new Set([
    query.minX,
    query.maxX,
    ...clipped.flatMap((bounds) => [bounds.minX, bounds.maxX]),
  ])].sort((left, right) => left - right)
  const epsilon = 1e-7
  for (let index = 1; index < xBreaks.length; index += 1) {
    const slabMinX = xBreaks[index - 1]
    const slabMaxX = xBreaks[index]
    if (slabMaxX - slabMinX <= epsilon) continue
    const slabX = (slabMinX + slabMaxX) * 0.5
    const intervals = clipped
      .filter((bounds) => slabX >= bounds.minX - epsilon && slabX <= bounds.maxX + epsilon)
      .map((bounds) => ({ min: bounds.minZ, max: bounds.maxZ }))
      .sort((left, right) => left.min - right.min || left.max - right.max)
    let coveredTo = query.minZ
    for (const interval of intervals) {
      if (interval.max < coveredTo - epsilon) continue
      if (interval.min > coveredTo + epsilon) return false
      coveredTo = Math.max(coveredTo, interval.max)
      if (coveredTo >= query.maxZ - epsilon) break
    }
    if (coveredTo < query.maxZ - epsilon) return false
  }
  return true
}

/**
 * Sweeps from the current position and returns the first unpublished point,
 * even when the requested destination happens to be published again beyond a
 * gap. This makes coverage loss a deterministic collision, not a fall-through.
 */
export function resolveMotionWithinPublishedCoverage(
  startX: number,
  startZ: number,
  endX: number,
  endZ: number,
  isPublished: (x: number, z: number) => boolean,
  maximumStepMeters = 0.5,
  refinementPasses = 12,
): ResolvedStreamingMotion {
  if (![startX, startZ, endX, endZ].every(Number.isFinite)) {
    return { x: startX, z: startZ, collided: true }
  }
  if (!isPublished(startX, startZ)) return { x: startX, z: startZ, collided: true }
  const dx = endX - startX
  const dz = endZ - startZ
  const distance = Math.hypot(dx, dz)
  if (distance <= 1e-9) return { x: endX, z: endZ, collided: false }
  const stepMeters = Number.isFinite(maximumStepMeters) && maximumStepMeters > 0 ? maximumStepMeters : 0.5
  const steps = Math.max(1, Math.ceil(distance / stepMeters))
  let lastSafeAmount = 0
  for (let step = 1; step <= steps; step += 1) {
    const amount = step / steps
    const x = startX + dx * amount
    const z = startZ + dz * amount
    if (isPublished(x, z)) {
      lastSafeAmount = amount
      continue
    }
    let safe = lastSafeAmount
    let blocked = amount
    for (let pass = 0; pass < Math.max(0, Math.floor(refinementPasses)); pass += 1) {
      const candidate = (safe + blocked) * 0.5
      if (isPublished(startX + dx * candidate, startZ + dz * candidate)) safe = candidate
      else blocked = candidate
    }
    return {
      x: startX + dx * safe,
      z: startZ + dz * safe,
      collided: true,
    }
  }
  return { x: endX, z: endZ, collided: false }
}

/**
 * Converts the conservative coverage sweep into streaming intent without
 * turning an asynchronous presentation boundary into collision geometry.
 *
 * The low-level sweep remains useful for proving whether the complete vehicle
 * footprint is published. Runtime traversal, however, is allowed to continue
 * to a finite destination while that result requests urgent prefetching. This
 * avoids the old invisible wall at the edge of a late or missing chunk.
 */
export function planStreamingTraversal(
  startX: number,
  startZ: number,
  endX: number,
  endZ: number,
  isPublished: (x: number, z: number) => boolean,
  maximumStepMeters = 0.5,
  refinementPasses = 12,
): StreamingTraversalPlan {
  if (![startX, startZ, endX, endZ].every(Number.isFinite)) {
    return { x: startX, z: startZ, coveragePending: true }
  }
  const coverage = resolveMotionWithinPublishedCoverage(
    startX,
    startZ,
    endX,
    endZ,
    isPublished,
    maximumStepMeters,
    refinementPasses,
  )
  return {
    x: endX,
    z: endZ,
    coveragePending: coverage.collided,
  }
}
